// @ts-nocheck
import { YStack, Text, Paragraph } from 'tamagui';
import { Container } from '../components/Container';
import { PageLayout } from '../components/PageLayout';

function Section({ title, children }) {
  return (
    <YStack gap="$3">
      <Text fontSize="$6" fontWeight="600" color="$color12">
        {title}
      </Text>
      {children}
    </YStack>
  );
}

function P({ children }) {
  return (
    <Paragraph fontSize="$4" color="$color8" lineHeight={26}>
      {children}
    </Paragraph>
  );
}

function BulletList({ items }) {
  return (
    <YStack gap="$2" paddingLeft="$4">
      {items.map((item, i) => (
        <XStackBullet key={i} text={item} />
      ))}
    </YStack>
  );
}

function XStackBullet({ text }) {
  return (
    <Paragraph fontSize="$4" color="$color8" lineHeight={26}>
      {'\u2022'} {text}
    </Paragraph>
  );
}

export default function PrivacyPage({ theme, toggleTheme }) {
  return (
    <PageLayout theme={theme} toggleTheme={toggleTheme}>
      <Container>
        <YStack paddingVertical="$8" gap="$8" maxWidth={700}>
          {/* Header */}
          <YStack gap="$4">
            <Text fontSize="$2" color="$color6" textTransform="uppercase" letterSpacing={3} fontWeight="500">
              Legal
            </Text>
            <Text fontSize={40} fontWeight="700" color="$color12" lineHeight={48}>
              Privacy Policy
            </Text>
            <Text fontSize="$3" color="$color7">
              Effective date: March 2026
            </Text>
          </YStack>

          <P>
            SID Technologies ("we", "us", "our") operates Torch, Statio, Denarius, JanusLedger,
            Pilum, and Scuta (collectively, "our products"). This Privacy Policy explains what data
            we collect, how we use it, and your rights regarding that data.
          </P>

          <Section title="What We Collect">
            <P>
              The data we collect depends on which products you use and how you interact with them.
            </P>
            <Text fontSize="$4" fontWeight="600" color="$color10" marginTop="$2">
              Account information
            </Text>
            <BulletList items={[
              'Email address (provided during signup or waitlist registration)',
              'Name and organization details (when you create an account)',
              'Authentication data managed through WorkOS',
            ]} />
            <Text fontSize="$4" fontWeight="600" color="$color10" marginTop="$2">
              Usage data
            </Text>
            <BulletList items={[
              'Product usage analytics (feature usage, page views, session duration)',
              'Error logs and performance metrics',
            ]} />
            <Text fontSize="$4" fontWeight="600" color="$color10" marginTop="$2">
              Billing data
            </Text>
            <BulletList items={[
              'Payment information processed securely through Stripe (we do not store card numbers)',
              'Subscription and invoice history',
            ]} />
          </Section>

          <Section title="Per-Product Data">
            <Text fontSize="$4" fontWeight="600" color="$color10">
              Torch
            </Text>
            <BulletList items={[
              'OAuth tokens for connected third-party applications (Gmail, Slack, Notion, etc.)',
              'Search index data derived from your connected apps',
              'Content you create within Torch (documents, notes, tasks, wiki pages)',
            ]} />
            <Text fontSize="$4" fontWeight="600" color="$color10" marginTop="$2">
              Statio
            </Text>
            <BulletList items={[
              'Vaulted API credentials for your MCP servers (encrypted at rest)',
              'Audit logs of API calls made through the gateway',
              'MCP server configuration and access policies',
            ]} />
            <Text fontSize="$4" fontWeight="600" color="$color10" marginTop="$2">
              Denarius
            </Text>
            <BulletList items={[
              'Portfolio data and holdings you configure',
              'Watchlists and screening criteria',
              'Analytics preferences and saved views',
            ]} />
            <Text fontSize="$4" fontWeight="600" color="$color10" marginTop="$2">
              JanusLedger
            </Text>
            <BulletList items={[
              'Ledger entries and transaction data',
              'Account structures and chart of accounts',
              'Reconciliation records and audit trails',
            ]} />
          </Section>

          <Section title="How We Use Your Data">
            <BulletList items={[
              'Provide, maintain, and improve our products',
              'Process payments and manage subscriptions',
              'Send product updates and security notices',
              'Send marketing communications (only if you opt in)',
              'Monitor and improve product performance and reliability',
              'Comply with legal obligations',
            ]} />
          </Section>

          <Section title="What We Don't Do">
            <BulletList items={[
              'We do not sell your personal data to anyone',
              'We do not share your data with third parties for their marketing purposes',
              'We do not use your product data to train AI models',
              'We do not provide your data to advertisers',
            ]} />
            <P>
              We only share data with service providers who are necessary to operate our products
              (see Third-Party Services below), and only to the extent required for them to perform
              their services.
            </P>
          </Section>

          <Section title="Data Storage and Security">
            <BulletList items={[
              'All data is encrypted at rest and in transit',
              'Infrastructure hosted on Google Cloud Platform (GCP Cloud Run), US-based regions',
              'Credentials and secrets are stored using industry-standard encryption',
              'Access to production systems is restricted and audited',
            ]} />
          </Section>

          <Section title="Third-Party Services">
            <P>We use the following third-party services to operate our products:</P>
            <BulletList items={[
              'WorkOS — Authentication and identity management',
              'Stripe — Payment processing and subscription management',
              'Cloudflare — DNS, CDN, and website hosting',
              'Google Cloud Platform — Application hosting and data storage',
            ]} />
            <P>
              Each of these providers has their own privacy policies and data handling practices.
              We encourage you to review them.
            </P>
          </Section>

          <Section title="Data Retention and Deletion">
            <P>
              We retain your data for as long as your account is active or as needed to provide
              you with our services. You may request deletion of your account and associated data
              at any time by contacting us. Upon deletion:
            </P>
            <BulletList items={[
              'Account information is permanently deleted within 30 days',
              'Product data (documents, ledger entries, portfolio data, etc.) is permanently deleted within 30 days',
              'Backup copies are purged within 90 days',
              'Billing records may be retained as required by law',
            ]} />
          </Section>

          <Section title="Your Rights">
            <P>You have the right to:</P>
            <BulletList items={[
              'Access the personal data we hold about you',
              'Request correction of inaccurate data',
              'Request deletion of your data',
              'Export your data in a portable format',
              'Opt out of marketing communications at any time',
            ]} />
          </Section>

          <Section title="Contact">
            <P>
              For privacy-related questions or requests, contact us at{' '}
              <Text
                color="$amber7" cursor="pointer"
                hoverStyle={{ opacity: 0.7 }}
                onPress={() => window.open('mailto:dans@romans.dev')}
              >
                dans@romans.dev
              </Text>
              .
            </P>
          </Section>

          <Section title="Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time. We will notify you of material
              changes by posting the updated policy on our website and updating the effective date.
              Continued use of our products after changes constitutes acceptance of the updated policy.
            </P>
          </Section>
        </YStack>
      </Container>
    </PageLayout>
  );
}
